import {GitHubAvatar} from '@github-ui/github-avatar'
import {CreateIssueDialogEntry, DisplayMode} from '@github-ui/issue-create'
import {MarkdownEditHistoryViewer} from '@github-ui/markdown-edit-history-viewer'
import {NestableActionMenu} from '@github-ui/nestable-action-menu'
import {userHovercardPath} from '@github-ui/paths'
import {
  EyeClosedIcon,
  EyeIcon,
  FoldIcon,
  IssueOpenedIcon,
  KebabHorizontalIcon,
  LinkIcon,
  PencilIcon,
  QuoteIcon,
  ReportIcon,
  TrashIcon,
  UnfoldIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, Box, Button, IconButton, Link, Octicon, RelativeTime, Text} from '@primer/react'
import {useCallback, useMemo, useState} from 'react'
import type {FragmentRefs} from 'relay-runtime'

import {LABELS, TEST_IDS} from '../constants'
import {type SelectionContext, selectQuoteFromComment} from '../utils/quotes'
import {CommentAuthorAssociation} from './CommentAuthorAssociation'
import type {CommentAuthorAssociation as GraphQlCommentAuthorAssociation} from './issue-comment/__generated__/IssueCommentHeader.graphql'
import {ReportContentDialog} from './issue-comment/ReportContentDialog'

interface CommentHeaderComment {
  authorAssociation: GraphQlCommentAuthorAssociation
  body: string
  id: string
  createdAt: string
  isHidden: boolean
  issue: {
    number: number
  }
  minimizedReason: string | null
  repository: {
    id: string
    isPrivate: boolean
    name: string
    owner: {
      login: string
      url: string
    }
  }
  url: string
  viewerCanDelete: boolean
  viewerCanMinimize: boolean
  viewerCanSeeMinimizeButton: boolean
  viewerCanSeeUnminimizeButton: boolean
  viewerCanUpdate: boolean
  viewerCanReport: boolean
  viewerCanReportToMaintainer: boolean
  author: {
    login: string
  } | null
  ' $fragmentSpreads': FragmentRefs<'MarkdownEditHistoryViewer_comment'>
}

type CommentHeaderProps = {
  comment: CommentHeaderComment
  login: string
  editComment: () => void
  onReplySelect: (quotedText?: string) => void
  isMinimized: boolean
  onMinimize?: (value: boolean) => void
  navigate: (url: string) => void
  avatarUrl: string
  hideComment: (reason: 'SPAM' | 'ABUSE' | 'OFF_TOPIC' | 'OUTDATED' | 'DUPLICATE' | 'RESOLVED') => void
  unhideComment: () => void
  deleteComment: () => void
  commentRef?: React.RefObject<HTMLDivElement>
}

export function CommentHeader({
  comment,
  login,
  editComment,
  onReplySelect,
  isMinimized = false,
  onMinimize: onMinimized,
  navigate,
  avatarUrl,
  hideComment,
  unhideComment,
  deleteComment,
  commentRef,
}: CommentHeaderProps) {
  const [menuOpened, setMenuOpened] = useState(false)
  const [selection, setSelection] = useState<SelectionContext | null>(null)

  const copyToClipboard = () => {
    navigator.clipboard.writeText(comment.url)
  }

  const minimizeButton = useMemo(() => {
    return !comment.isHidden || isMinimized || !comment.viewerCanSeeMinimizeButton ? null : (
      <Button
        sx={{color: 'fg.muted', p: 1}}
        aria-label={'hide comment'}
        variant={'invisible'}
        onClick={() => onMinimized?.(true)}
      >
        <Octicon icon={FoldIcon} />
      </Button>
    )
  }, [comment.isHidden, isMinimized, onMinimized, comment.viewerCanSeeMinimizeButton])

  const unminimizeButton = useMemo(() => {
    return !comment.isHidden || !isMinimized || !comment.viewerCanSeeUnminimizeButton ? null : (
      <Button
        sx={{color: 'fg.muted', p: 1}}
        aria-label={'show comment'}
        variant={'invisible'}
        onClick={() => onMinimized?.(false)}
      >
        <Octicon icon={UnfoldIcon} />
      </Button>
    )
  }, [comment.isHidden, isMinimized, onMinimized, comment.viewerCanSeeUnminimizeButton])

  const [isNewIssueOpen, setIsNewIssueOpen] = useState(false)
  const [isReportContentOpen, setIsReportContentOpen] = useState(false)

  const formatBodyAsReference = useCallback(
    (str: string) => {
      return `${str.replace(/^(?!>)/gm, '> ')} \n\n _Originally posted by @${login} in [#${comment.issue.number}](${
        comment.url
      })_`
    },
    [comment.issue.number, login, comment.url],
  )

  const reportUrl = useMemo(() => {
    if (!comment.author) return ''
    const host = window.location.origin
    const query = `content_url=${encodeURIComponent(comment.url)}&report=${comment.author.login}+(user)`
    const url = `${host}/contact/report-content?${query}`

    return url
  }, [comment.author, comment.url])

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: comment.isHidden ? 'center' : ['center', 'center', 'flex-start'],
        gap: 2,
      }}
    >
      {isNewIssueOpen && (
        <CreateIssueDialogEntry
          isCreateDialogOpen={isNewIssueOpen}
          setIsCreateDialogOpen={setIsNewIssueOpen}
          navigate={navigate}
          optionConfig={{
            defaultDisplayMode: DisplayMode.IssueCreation,
            issueCreateArguments: {
              initialValues: {
                body: formatBodyAsReference(comment.body),
              },
              repository: {
                name: comment.repository.name,
                owner: comment.repository.owner.login,
              },
            },
          }}
        />
      )}
      {isReportContentOpen && (
        <ReportContentDialog
          owner={comment.repository.owner.login}
          ownerUrl={comment.repository.owner.url}
          reportUrl={reportUrl}
          onClose={() => setIsReportContentOpen(false)}
        />
      )}
      <h3 className="sr-only">
        {login} commented <RelativeTime date={new Date(comment.createdAt)} />
      </h3>
      <Box
        data-testid={TEST_IDS.commentHeaderLeftSideItems}
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          pt: comment.isHidden ? 0 : 2,
        }}
      >
        <GitHubAvatar
          data-hovercard-url={userHovercardPath({owner: login})}
          src={avatarUrl}
          size={24}
          alt={`@${login}`}
          sx={{
            flexShrink: 0,
            opacity: comment.isHidden ? '0.5' : '1',
            display: ['none', 'block', 'block', 'block'],
          }}
        />
        <GitHubAvatar
          data-hovercard-url={userHovercardPath({owner: login})}
          src={avatarUrl}
          size={32}
          alt={`@${login}`}
          sx={{
            flexShrink: 0,
            opacity: comment.isHidden ? '0.5' : '1',
            display: ['block', 'none', 'none', 'none'],
          }}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: ['column', 'row', 'row', 'row'],
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            columnGap: 1,
          }}
        >
          <Link
            data-testid={TEST_IDS.avatarLink}
            href={`/${login}`}
            data-hovercard-url={userHovercardPath({owner: login})}
            sx={{
              fontSize: '14px',
              color: comment.isHidden ? 'fg.muted' : 'fg.default',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {login}
          </Link>
          <Text sx={{fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis'}}>
            <Link href={comment.url} sx={{color: 'fg.muted'}}>
              <RelativeTime date={new Date(comment.createdAt)} />
            </Link>
          </Text>
        </Box>
      </Box>

      <Box data-testid={TEST_IDS.commentHeaderRightSideItems} sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
        {comment.isHidden && (
          <Box sx={{display: 'flex', gap: 2, alignItems: 'center', color: 'fg.muted'}}>
            <Text sx={{fontSize: 0}}>
              {comment.minimizedReason
                ? `${LABELS.hiddenCommentWithReason} ${comment.minimizedReason.replace(/_/g, '-').toLowerCase()}`
                : LABELS.hiddenComment}
            </Text>
          </Box>
        )}

        {!comment.repository.isPrivate && !isMinimized && (
          <CommentAuthorAssociation association={comment.authorAssociation} />
        )}
        {minimizeButton}
        {unminimizeButton}
        <MarkdownEditHistoryViewer editHistory={comment} />
        <ActionMenu
          open={menuOpened}
          onOpenChange={(value: boolean) => {
            if (value) {
              const current = window.getSelection()
              if (current && current.anchorNode) {
                setSelection({
                  anchorNode: current.anchorNode,
                  range: current.getRangeAt(0),
                })
              } else {
                setSelection(null)
              }
            }
            setMenuOpened(value)
          }}
        >
          <ActionMenu.Anchor data-testid={TEST_IDS.commentHeaderHamburger}>
            <IconButton
              size="small"
              icon={KebabHorizontalIcon}
              variant="invisible"
              aria-label="Comment actions"
              sx={{color: 'fg.muted'}}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList data-testid={TEST_IDS.commentHeaderHamburgerOpen}>
              <ActionList.Item onSelect={() => copyToClipboard()}>
                <ActionList.LeadingVisual>
                  <LinkIcon />
                </ActionList.LeadingVisual>
                Copy link
              </ActionList.Item>
              <ActionList.Item
                onSelect={() => {
                  const newValue = selectQuoteFromComment(commentRef?.current, selection)
                  onReplySelect(newValue)
                }}
              >
                <ActionList.LeadingVisual>
                  <QuoteIcon />
                </ActionList.LeadingVisual>
                Quote reply
              </ActionList.Item>
              <ActionList.Divider />
              <ActionList.Item
                data-testid={TEST_IDS.commentMenuRefComment}
                sx={{width: 'max-content'}}
                onSelect={() => setIsNewIssueOpen(true)}
              >
                <ActionList.LeadingVisual>
                  <IssueOpenedIcon />
                </ActionList.LeadingVisual>
                Reference in a new issue
              </ActionList.Item>

              {(comment.viewerCanUpdate || comment.viewerCanMinimize || comment.viewerCanDelete) && (
                <ActionList.Divider />
              )}
              {comment.viewerCanUpdate && (
                <ActionList.Item onSelect={() => editComment()}>
                  <ActionList.LeadingVisual>
                    <PencilIcon />
                  </ActionList.LeadingVisual>
                  Edit
                </ActionList.Item>
              )}
              {comment.viewerCanMinimize && !comment.isHidden && (
                <NestableActionMenu
                  anchor={
                    <NestableActionMenu.MenuItemAnchor leadingVisual={EyeClosedIcon}>
                      Hide
                    </NestableActionMenu.MenuItemAnchor>
                  }
                >
                  <ActionList.Item
                    aria-label="Hide comment as spam"
                    onSelect={() => {
                      setMenuOpened(false)
                      hideComment('SPAM')
                    }}
                  >
                    Spam
                  </ActionList.Item>
                  <ActionList.Item
                    aria-label="Hide comment as abuse"
                    onSelect={() => {
                      setMenuOpened(false)
                      hideComment('ABUSE')
                    }}
                  >
                    Abuse
                  </ActionList.Item>
                  <ActionList.Item
                    aria-label="Hide comment as off-topic"
                    onSelect={() => {
                      setMenuOpened(false)
                      hideComment('OFF_TOPIC')
                    }}
                  >
                    Off-topic
                  </ActionList.Item>
                  <ActionList.Item
                    aria-label="Hide comment as outdated"
                    onSelect={() => {
                      setMenuOpened(false)
                      hideComment('OUTDATED')
                    }}
                  >
                    Outdated
                  </ActionList.Item>
                  <ActionList.Item
                    aria-label="Hide comment as duplicate"
                    onSelect={() => {
                      setMenuOpened(false)
                      hideComment('DUPLICATE')
                    }}
                  >
                    Duplicate
                  </ActionList.Item>
                  <ActionList.Item
                    aria-label="Hide comment as resolved"
                    onSelect={() => {
                      setMenuOpened(false)
                      hideComment('RESOLVED')
                    }}
                  >
                    Resolved
                  </ActionList.Item>
                </NestableActionMenu>
              )}
              {comment.viewerCanMinimize && comment.isHidden && (
                <ActionList.Item onSelect={unhideComment}>
                  <ActionList.LeadingVisual>
                    <EyeIcon />
                  </ActionList.LeadingVisual>{' '}
                  Unhide
                </ActionList.Item>
              )}
              {comment.viewerCanDelete && (
                <ActionList.Item variant="danger" onSelect={deleteComment}>
                  <ActionList.LeadingVisual>
                    <TrashIcon />
                  </ActionList.LeadingVisual>
                  Delete
                </ActionList.Item>
              )}
              {(comment.viewerCanReport || comment.viewerCanReportToMaintainer) && comment.author && (
                <>
                  <ActionList.Divider />
                  {comment.viewerCanReportToMaintainer ? (
                    // Commenting this out until the report-to-maintainer functionality is implemented in the dialog component
                    // <ActionList.Item
                    //   onSelect={() => {
                    //     setIsReportContentOpen(true)
                    //   }}
                    // >
                    //   <ActionList.LeadingVisual>
                    //     <ReportIcon />
                    //   </ActionList.LeadingVisual>
                    //   Report content
                    // </ActionList.Item>
                    <ActionList.LinkItem href={reportUrl} target="_blank">
                      <ActionList.LeadingVisual>
                        <ReportIcon />
                      </ActionList.LeadingVisual>
                      Report content
                    </ActionList.LinkItem>
                  ) : (
                    <ActionList.LinkItem href={reportUrl} target="_blank">
                      <ActionList.LeadingVisual>
                        <ReportIcon />
                      </ActionList.LeadingVisual>
                      Report content
                    </ActionList.LinkItem>
                  )}
                </>
              )}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </Box>
    </Box>
  )
}

try{ CommentHeader.displayName ||= 'CommentHeader' } catch {}